// ═══════════════════════════════════════════════════════════════════════════
// PersistentPlayer — bottom-of-viewport audio bar.
//
// The bar shows anywhere a track is active (home collections, profiles,
// the workshop) and stays until the user closes it with X. Audio keeps
// playing across every route change.
//
// Mounted ONCE inside <PlayerProvider> at the App root so the audio
// element + state survive any in-route re-renders.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState } from 'react';
import { usePlayer } from './PlayerContext';
import { storageImage } from '../../lib/img';

export default function PersistentPlayer() {
  const player = usePlayer();
  const audioRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  // Lazily resolve a track's audio URL. Collection playlist tracks arrive
  // without a url and a resolve() the player calls when it lands on them, so
  // we don't pre-generate every episode's audio. The resolved url is cached
  // back onto the track object so re-visiting it doesn't regenerate.
  const [resolvedUrl, setResolvedUrl] = useState(null);
  useEffect(() => {
    const t = player.track;
    if (!t) { setResolvedUrl(null); return undefined; }
    if (t.url) { setResolvedUrl(t.url); return undefined; }
    if (typeof t.resolve !== 'function') { setResolvedUrl(null); return undefined; }
    let alive = true;
    setResolvedUrl(null); // loading — no src until it resolves
    t.resolve()
      .then((url) => { if (alive && url) { t.url = url; setResolvedUrl(url); } })
      .catch(() => {
        // This episode can't be narrated (no script yet). Don't freeze the
        // bar: roll to the next queued preview, or stop if it's the last one.
        if (!alive) return;
        if (player.hasNext) player.next();
        else player.pause();
      });
    return () => { alive = false; };
  }, [player.track]);

  // Sync the audio element's play/pause state with the context's intent.
  // Includes resolvedUrl so playback (re)starts once a lazy src lands.
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !player.track) return;
    if (player.playing && (resolvedUrl || player.track.url)) {
      const p = a.play();
      if (p && p.catch) p.catch(() => {/* autoplay blocked — user can re-tap */});
    } else if (!player.playing) {
      a.pause();
    }
  }, [player.playing, player.track, resolvedUrl]);

  // Reset progress when the track changes (identity, not url — url mutates
  // in place when a lazy track resolves, which shouldn't reset).
  useEffect(() => {
    setProgress(0);
    setDuration(0);
  }, [player.track]);

  function onTime() {
    const a = audioRef.current;
    if (!a) return;
    setProgress(a.currentTime || 0);
    // Preview window (collections): cut to the next episode after ~60s.
    const preview = player.track?.previewSeconds;
    if (preview && a.currentTime >= preview) {
      if (player.hasNext) player.next();
      else player.pause();
    }
  }
  function onLoadedMeta() {
    const a = audioRef.current;
    if (a) setDuration(a.duration || 0);
  }
  function onEnded() {
    // Roll into the next episode in the queue if there is one; otherwise stop.
    if (player.hasNext) player.next();
    else player.pause();
  }
  function onSeek(e) {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = Math.max(0, Math.min(duration, pct * duration));
    setProgress(a.currentTime);
  }

  // The bar shows anywhere a track is active and stays until the user
  // closes it with X (audio keeps playing across route changes).
  if (!player.visible || !player.track) return null;

  // Always-mounted audio so seeking works while minimized.
  const audioEl = (
    <audio
      ref={audioRef}
      src={resolvedUrl || player.track.url || undefined}
      preload="metadata"
      onTimeUpdate={onTime}
      onLoadedMetadata={onLoadedMeta}
      onEnded={onEnded}
      style={{ display: 'none' }}
    />
  );

  if (player.minimized) {
    return (
      <div className="pp-minimized" role="region" aria-label="Audio player (minimized)">
        <button
          type="button"
          className="pp-min-toggle"
          onClick={player.toggle}
          aria-label={player.playing ? 'Pause' : 'Play'}
        >
          {player.playing ? <PauseGlyph /> : <PlayGlyph />}
        </button>
        <button
          type="button"
          className="pp-min-expand"
          onClick={player.expand}
          aria-label="Expand audio player"
        >
          <span className="pp-min-title">{player.track.title || 'Now playing'}</span>
          <ChevronUpGlyph />
        </button>
        <button
          type="button"
          className="pp-min-close"
          onClick={player.close}
          aria-label="Close audio player"
        >×</button>
        {audioEl}
      </div>
    );
  }

  const pct = duration > 0 ? (progress / duration) * 100 : 0;
  return (
    <div className="pp-bar" role="region" aria-label="Audio player">
      <div className="pp-cover" aria-hidden>
        {player.track.coverUrl
          ? <img src={storageImage(player.track.coverUrl, { width: 96 })} alt="" />
          : <span className="pp-cover-fallback"><WaveGlyph /></span>}
      </div>

      <div className="pp-transport">
        <button
          type="button"
          className="pp-skip"
          onClick={player.prev}
          disabled={!player.hasPrev}
          aria-label="Previous"
          title="Previous"
        >
          <PrevGlyph />
        </button>
        <button
          type="button"
          className="pp-play"
          onClick={player.toggle}
          aria-label={player.playing ? 'Pause' : 'Play'}
        >
          {player.playing ? <PauseGlyph /> : <PlayGlyph />}
        </button>
        <button
          type="button"
          className="pp-skip"
          onClick={player.next}
          disabled={!player.hasNext}
          aria-label="Next"
          title="Next"
        >
          <NextGlyph />
        </button>
      </div>

      <div className="pp-meta">
        {player.track.onCreatorClick ? (
          <button type="button" className="pp-title pp-title--link" onClick={player.track.onCreatorClick} title="Open episode / creator">
            {player.track.title || 'Now playing'}
          </button>
        ) : (
          <div className="pp-title">{player.track.title || 'Now playing'}</div>
        )}
        {player.track.subtitle && (
          player.track.onCreatorClick ? (
            <button type="button" className="pp-subtitle pp-subtitle--link" onClick={player.track.onCreatorClick} title="Open creator profile">
              {player.track.subtitle}
            </button>
          ) : (
            <div className="pp-subtitle">{player.track.subtitle}</div>
          )
        )}
        <div
          className="pp-progress"
          onClick={onSeek}
          role="slider"
          aria-label="Seek"
          aria-valuemin="0"
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(progress)}
        >
          <div className="pp-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="pp-times">
          <span>{fmtTime(progress)}</span>
          <span>{fmtTime(duration)}</span>
        </div>
      </div>

      <div className="pp-actions">
        {player.track.onExport && (
          <button
            type="button"
            className="pp-icon-btn pp-export-btn"
            onClick={() => player.track.onExport()}
            aria-label="Export MP3"
            title="Export as MP3 (1 credit)"
          >
            <DownloadGlyph />
            <span className="pp-export-label">Export</span>
          </button>
        )}
        <button
          type="button"
          className="pp-icon-btn"
          onClick={player.minimize}
          aria-label="Minimize player"
          title="Minimize"
        >
          <ChevronDownGlyph />
        </button>
        <button
          type="button"
          className="pp-icon-btn"
          onClick={player.close}
          aria-label="Close player"
          title="Close"
        >×</button>
      </div>

      {audioEl}
    </div>
  );
}

// ─── Glyphs ──────────────────────────────────────────────────────────────
function PlayGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <polygon points="6,4 20,12 6,20" />
    </svg>
  );
}
function PauseGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  );
}
function PrevGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <polygon points="18,5 8,12 18,19" />
      <rect x="5" y="5" width="2.4" height="14" rx="1" />
    </svg>
  );
}
function NextGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <polygon points="6,5 16,12 6,19" />
      <rect x="16.6" y="5" width="2.4" height="14" rx="1" />
    </svg>
  );
}
function DownloadGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3v11" /><polyline points="7 10 12 15 17 10" /><path d="M5 20h14" />
    </svg>
  );
}
function ChevronDownGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
function ChevronUpGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );
}
function WaveGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
      <line x1="6"  y1="9"  x2="6"  y2="15" />
      <line x1="10" y1="6"  x2="10" y2="18" />
      <line x1="14" y1="8"  x2="14" y2="16" />
      <line x1="18" y1="10" x2="18" y2="14" />
    </svg>
  );
}

function fmtTime(s) {
  const sec = Math.max(0, Math.floor(s || 0));
  const m = Math.floor(sec / 60);
  const r = sec % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}
